import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { db } from "../../../firebase";
import { useAuth } from "../../components/authContext";

interface SessionLog {
  startTime: string;
  endTime: string;
  durationSeconds: number;
  notes?: string | null;
  createdAt: { seconds: number };
}

const GreTimer: React.FC = () => {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [elapsed, setElapsed] = useState(0); // ms
  const [now, setNow] = useState(new Date());
  const [logs, setLogs] = useState<SessionLog[]>([]);
  const [notes, setNotes] = useState("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchLogs = React.useCallback(async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, `users/${user.uid}/greLogs`),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((doc) => doc.data() as SessionLog);
      setLogs(items);
    } catch (error) {
      console.error(
        "⚠️ Firestore index may be missing. Run the query to trigger index setup.",
        error
      );
    }
  }, [user]);

  useEffect(() => {
    fetchLogs();
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(clock);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, fetchLogs]);

  const startTimer = () => {
    const now = new Date();
    setStartTime(now);
    setEndTime(null);
    setElapsed(0);
    setIsRunning(true);
    setNotes("");
    intervalRef.current = setInterval(() => {
      setElapsed(Date.now() - now.getTime());
    }, 1000);
  };

  const pauseTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsRunning(false);
  };

  const resumeTimer = () => {
    const resumed = new Date(Date.now() - elapsed);
    setStartTime(resumed);
    setIsRunning(true);
    intervalRef.current = setInterval(() => {
      setElapsed(Date.now() - resumed.getTime());
    }, 1000);
  };

  const finishTimer = async () => {
    if (!user || !startTime) return;
    if (intervalRef.current) clearInterval(intervalRef.current);

    const currentEnd = new Date();
    const durationSeconds = Math.floor(elapsed / 1000);

    await addDoc(collection(db, `users/${user.uid}/greLogs`), {
      uid: user.uid,
      startTime: startTime.toISOString(),
      endTime: currentEnd.toISOString(),
      durationSeconds,
      notes: notes.trim() || null,
      createdAt: serverTimestamp(),
    });

    alert(`Session logged: ${formatElapsed(elapsed)}`);
    setIsRunning(false);
    setStartTime(null);
    setEndTime(currentEnd);
    setElapsed(0);
    setNotes("");
    fetchLogs();
  };

  const formatTime = (date: Date | null) =>
    date
      ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "--:--";

  const formatElapsed = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const groupLogsByDay = (logs: SessionLog[]) => {
    const grouped: Record<string, SessionLog[]> = {};
    for (const log of logs) {
      const dateStr = new Date(log.createdAt.seconds * 1000).toDateString();
      if (!grouped[dateStr]) grouped[dateStr] = [];
      grouped[dateStr].push(log);
    }
    return grouped;
  };

  const totalAllTimeSeconds = logs.reduce(
    (acc, log) => acc + (log.durationSeconds || 0),
    0
  );

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>GRE Time Tracker</h1>

      <div style={styles.timeInfo}>
        <p>
          🕒 <strong>Now:</strong> {formatTime(now)}
        </p>
        <p>
          ▶️ <strong>Start:</strong> {formatTime(startTime)}
        </p>
        <p>
          ⏹ <strong>End:</strong> {formatTime(endTime)}
        </p>
      </div>

      <div style={styles.timer}>{formatElapsed(elapsed)}</div>

      <div style={styles.controls}>
        {!isRunning && elapsed === 0 && (
          <button onClick={startTimer} style={styles.button}>
            Start
          </button>
        )}
        {!isRunning && elapsed > 0 && (
          <button onClick={resumeTimer} style={styles.button}>
            Resume
          </button>
        )}
        {isRunning && (
          <button onClick={pauseTimer} style={styles.button}>
            Pause
          </button>
        )}
        <button onClick={finishTimer} style={styles.finishButton}>
          Finish & Log
        </button>
      </div>

      <textarea
        placeholder="Add optional notes..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        style={{
          width: "100%",
          minHeight: "60px",
          marginTop: "10px",
          padding: "10px",
          borderRadius: "6px",
          border: "1px solid #ccc",
        }}
      />

      <h2 style={{ marginTop: "40px" }}>Total Study Time</h2>
      <p style={{ fontSize: "18px", fontWeight: "bold" }}>
        {formatElapsed(totalAllTimeSeconds * 1000)}
      </p>

      <h2>Past Sessions</h2>
      {Object.entries(groupLogsByDay(logs)).map(([date, entries], i) => (
        <div key={i} style={{ marginBottom: "20px" }}>
          <h3>{date}</h3>
          <ul>
            {entries.map((log, j) => (
              <li key={j} style={{ marginBottom: "8px" }}>
                <div>
                  {new Date(log.startTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  -{" "}
                  {new Date(log.endTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  ({formatElapsed(log.durationSeconds * 1000)})
                </div>
                {log.notes && (
                  <div
                    style={{
                      fontStyle: "italic",
                      fontSize: "14px",
                      color: "#555",
                    }}
                  >
                    📝 {log.notes}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "600px",
    margin: "40px auto",
    fontFamily: "sans-serif",
    padding: "20px",
  },
  title: {
    fontSize: "28px",
    marginBottom: "10px",
    textAlign: "center" as const,
  },
  timeInfo: {
    fontSize: "16px",
    marginBottom: "10px",
    textAlign: "center" as const,
    lineHeight: 1.6,
  },
  timer: {
    fontSize: "42px",
    fontWeight: "bold" as const,
    textAlign: "center" as const,
    marginBottom: "20px",
  },
  controls: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    alignItems: "center" as const,
    marginBottom: "20px",
  },
  button: {
    padding: "10px 20px",
    fontSize: "16px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  finishButton: {
    padding: "10px 20px",
    fontSize: "16px",
    backgroundColor: "#f44336",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
};

export default GreTimer;
